import { useCallback, useEffect, useState } from 'react';
import { Loader2, Sparkles, Check, Plus, RefreshCw } from 'lucide-react';
import { Label } from '@easyflow/ui-frontend/components/ui/label';
import { Badge } from '@easyflow/ui-frontend/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { apiClient } from '@/lib/api';

/**
 * Task #2685: assegnazione Claude Skills per-agente.
 * Le skill assegnate qui vengono iniettate a runtime dal backend come
 * `container.skills` (+ tool code_execution + 3 beta) sulle chiamate Claude
 * dell'agente (Personal Assistant, Chatbot, nodo Workflow "Claude Agent").
 */
interface ClaudeSkill {
  id: string;
  tenant_id: string;
  anthropic_skill_id: string;
  display_title: string;
  description: string | null;
  source: string;
  latest_version: string | null;
  created_at: string;
  updated_at: string;
}

interface AgentClaudeSkillsProps {
  /** ID dell'ai_agent (NON il wrapper assistant/chatbot). */
  agentId: string;
}

function getErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  return String(error);
}

export function AgentClaudeSkills({ agentId }: AgentClaudeSkillsProps) {
  const { toast } = useToast();
  const [allSkills, setAllSkills] = useState<ClaudeSkill[]>([]);
  const [assignedIds, setAssignedIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [skillsRes, assignedRes] = await Promise.all([
        apiClient.get<ClaudeSkill[]>('/ai-tools/claude-config/skills'),
        apiClient.get<ClaudeSkill[]>(`/ai-tools/claude-config/agent-skills/${agentId}`),
      ]);
      const skillsData = skillsRes?.data;
      const assignedData = assignedRes?.data;
      setAllSkills(Array.isArray(skillsData) ? skillsData : []);
      setAssignedIds(new Set(Array.isArray(assignedData) ? assignedData.map((s) => s.id) : []));
    } catch (error) {
      toast({ title: 'Errore', description: `Impossibile caricare le Skill: ${getErrorMessage(error)}`, variant: 'destructive' });
      setAllSkills([]);
      setAssignedIds(new Set());
    } finally {
      setLoading(false);
    }
  }, [agentId, toast]);

  useEffect(() => {
    void load();
  }, [load]);

  const toggle = async (skill: ClaudeSkill) => {
    const isAssigned = assignedIds.has(skill.id);
    setBusyId(skill.id);
    try {
      if (isAssigned) {
        await apiClient.delete(`/ai-tools/claude-config/skills/${skill.id}/assign/${agentId}`);
        setAssignedIds((prev) => {
          const next = new Set(prev);
          next.delete(skill.id);
          return next;
        });
      } else {
        await apiClient.post(`/ai-tools/claude-config/skills/${skill.id}/assign`, { agent_id: agentId });
        setAssignedIds((prev) => new Set(prev).add(skill.id));
      }
    } catch (error) {
      toast({ title: 'Errore', description: `Operazione non riuscita: ${getErrorMessage(error)}`, variant: 'destructive' });
    } finally {
      setBusyId(null);
    }
  };

  const assignedCount = assignedIds.size;

  return (
    <div className="space-y-2 pt-1 border-t border-gray-200 dark:border-gray-700">
      <div className="flex items-center justify-between">
        <Label className="text-xs flex items-center gap-1.5">
          <Sparkles className="h-3.5 w-3.5 text-purple-500" />
          Claude Skills
        </Label>
        <div className="flex items-center gap-2">
          <span className="text-[10px] text-gray-400">{assignedCount > 0 ? `${assignedCount} assegnate (max 8)` : 'Nessuna assegnata'}</span>
          <button
            type="button"
            onClick={() => void load()}
            disabled={loading}
            className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
            title="Ricarica"
          >
            <RefreshCw className={`h-3 w-3 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>
      <p className="text-[10px] text-gray-400 -mt-1">
        Le skill assegnate vengono caricate nel container Claude di questo agente (richiede code execution, attivato automaticamente).
      </p>

      {loading ? (
        <div className="flex items-center justify-center py-4 text-gray-400">
          <Loader2 className="h-4 w-4 animate-spin" />
        </div>
      ) : allSkills.length === 0 ? (
        <p className="text-[10px] text-muted-foreground italic py-2">
          Nessuna Skill disponibile. Creane una dalla configurazione Claude del tenant.
        </p>
      ) : (
        <div className="grid grid-cols-1 gap-1.5 max-h-56 overflow-y-auto">
          {allSkills.map((skill) => {
            const isAssigned = assignedIds.has(skill.id);
            const isBusy = busyId === skill.id;
            const atCap = !isAssigned && assignedCount >= 8;
            return (
              <button
                key={skill.id}
                type="button"
                onClick={() => void toggle(skill)}
                disabled={isBusy || atCap}
                className={`flex items-start justify-between gap-2 p-1.5 rounded text-left border transition-all ${
                  isAssigned
                    ? 'bg-purple-50 dark:bg-purple-950/30 border-purple-300 dark:border-purple-800'
                    : 'bg-gray-50 dark:bg-gray-800/40 border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-800'
                } ${atCap ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs font-medium truncate">{skill.display_title}</span>
                    {skill.latest_version && (
                      <Badge variant="outline" className="text-[9px] px-1 py-0 h-3.5">v{skill.latest_version}</Badge>
                    )}
                  </div>
                  {skill.description && (
                    <p className="text-[10px] text-muted-foreground leading-tight truncate">{skill.description}</p>
                  )}
                </div>
                <span className="shrink-0 mt-0.5">
                  {isBusy ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin text-gray-400" />
                  ) : isAssigned ? (
                    <Check className="h-3.5 w-3.5 text-purple-600" />
                  ) : (
                    <Plus className="h-3.5 w-3.5 text-gray-400" />
                  )}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
